import React, { useState } from 'react';
import { Modal, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import * as Yup from 'yup';
import { t } from '../../services/translations';
import { useMutation } from '@tanstack/react-query';
import { generateLLM } from '../../services/api/llm.api';
import useLoggedUser from '../../hooks/useLoggedUser';
import { BACKGROUND_COLOR, DARK_GRAY, LIGHT_GRAY } from '../../styles/appColor';
import { AppButton, Type } from '../atomic/AppButton';
import { FormField } from '../atomic/FormField';
import { AppTextInput } from '../atomic/AppTextInput';
import { Icon } from '@rneui/base';

export type FormType = {
  prompt: string;
};

export const schema = Yup.object({
  prompt: Yup.string().min(1, t('field_required')).required(t('field_required')),
});

export type AIModalProps = {
  isVisible: boolean;
  onGenerated: (value: string) => void;
  onClose: () => void;
};

export function AIModal({ isVisible, onGenerated, onClose }: AIModalProps) {
  const loggedUser = useLoggedUser();
  const [prompt, setPrompt] = useState('');

  const { mutate, isLoading } = useMutation({
    mutationFn: (values: FormType) =>
      generateLLM(loggedUser, {
        messages: [
          {
            role: 'system',
            content: t('preprompt_no_introduction_or_ending'),
          },
          {
            role: 'user',
            content: values.prompt,
          },
        ],
      }),
    onSuccess: (response) => {
      onGenerated(response.content);
      onClose();
    },
  });

  return (
    <Modal visible={isVisible} onDismiss={onClose} transparent>
      <View style={styles.background}>
        <Pressable onPress={onClose} style={styles.closeBackground} />
        <View style={styles.container}>
          <View style={styles.closeContainer}>
            <TouchableOpacity onPress={onClose}>
              <Icon name="close" color={DARK_GRAY} />
            </TouchableOpacity>
          </View>
          <FormField title={t('enter_a_prompt')}>
            <AppTextInput
              value={prompt}
              onChangeText={setPrompt}
              displayOnly={isLoading}
              style={isLoading ? styles.opacityHalf : undefined}
              multiline
            />
          </FormField>
          <AppButton
            title={t('send')}
            type={Type.PRIMARY}
            onPress={() => mutate({ prompt })}
            enabled={!!prompt && !isLoading}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  background: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  closeBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  container: {
    maxWidth: 420,
    backgroundColor: BACKGROUND_COLOR,
    width: '100%',
    padding: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: LIGHT_GRAY,
  },
  closeContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  title: { textAlign: 'center', paddingVertical: 20, fontSize: 25 },
  opacityHalf: {
    opacity: 0.5,
  },
});
